import { Modal } from '@hologram-dimension/modal';
import { useCallback, useId, useState, type FC } from 'react';
import CoverageSearch from 'components/CoverageSearch/CoverageSearch';
import { Button } from '@hologram-dimension/button';
import { DropdownPanel } from '@hologram-dimension/dropdown-panel';
import { EnumSimInventoryExportType, exportTypeToLabel } from 'types/common';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import usePlan from 'api/legacy/plans/usePlan';
import useOrganization from 'state/contexts/Organization/useOrganization';
import useBillingRates from 'api/billing/useBillingRates';
import type { LegacyPlan } from 'types/legacyDashboard';
import useAlgoliaFilter from 'components/CoverageSearch/useAlgoliaFilter';
import exportCoverageData from 'utils/coverage/exportCoverageData';
import useCoverageProfiles from 'components/CoverageSearch/useCoverageProfiles';
import type { CommonModalProps } from '../shared';
import styles from './CoverageSearchModal.module.css';

interface CoverageSearchModalProps extends CommonModalProps {
  legacyPlan?: LegacyPlan;
}

const CoverageSearchModal: FC<CoverageSearchModalProps> = ({
  onClose,
  defaultOpen = false,
  legacyPlan,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const { sendAnalyticsEvent, sendAnalyticsError } = useAnalytics();
  const orgId = useOrganization();
  const { plan, error: planError, isLoading: isPlanLoading } = usePlan(legacyPlan?.id);
  const {
    billingRates,
    error: billingRatesError,
    isLoading: isBillingRatesLoading,
  } = useBillingRates(orgId);

  const isLoading = isPlanLoading || isBillingRatesLoading;
  const error = planError || billingRatesError;

  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [showExport, setShowExport] = useState<boolean>(false);
  const onExportClick = useCallback(() => {
    setShowExport((prev) => !prev);
  }, []);
  const exportButtonId = useId();
  const profiles = useCoverageProfiles({ planProfiles: plan?.profiles });
  const filters = useAlgoliaFilter({ planProfiles: plan?.profiles });
  const handleExport = async (exportType: EnumSimInventoryExportType) => {
    setIsExporting(true);
    onExportClick();
    try {
      const exportFn = await exportCoverageData({
        filters,
        profiles,
        legacyPlan,
        billingRates,
        onSuccess: (type) => {
          if (sendAnalyticsEvent) {
            sendAnalyticsEvent(`Network Coverage - Export Networks - Export Networks success`, {
              exportType: exportTypeToLabel(type),
            });
          }
        },
        onError: (err) => {
          if (sendAnalyticsError) {
            sendAnalyticsError('Export Network Coverage error', err);
          }
        },
      });

      await exportFn(exportType);
      return setIsExporting(false);
    } catch (e) {
      setIsExporting(false);
      return false;
    }
  };

  return (
    <Modal
      className={styles.modal}
      isLoading={isLoading}
      noContentPadding
      header={`${legacyPlan?.planName} – Network coverage and rates`}
      onClosed={onClose}
      open={open}
      setOpen={setOpen}
      contentWell={
        <div className={styles.wellContent}>
          <span>
            Networks you can connect to are determined by your SIM and the profile(s) installed on
            it. Some data plans provide coverage across multiple types of SIMs and profiles.
          </span>
          <div className={styles.wellActions}>
            <DropdownPanel
              className={styles.exportMenu}
              content={[
                {
                  label: `Export coverage and rates`,
                  items: [
                    {
                      children: 'Export to XLSX (Excel)',
                      iconStart: 'DownloadTray',
                      onClick: () => handleExport(EnumSimInventoryExportType.XLSX),
                    },
                    {
                      children: 'Export to CSV',
                      iconStart: 'DownloadTray',
                      onClick: () => handleExport(EnumSimInventoryExportType.CSV),
                    },
                  ],
                },
              ]}
              open={showExport}
              onOpenChange={setShowExport}
            >
              <Button
                iconEnd={showExport ? 'ChevronSingleNorth' : 'ChevronSingleSouth'}
                variant="secondary"
                onClick={onExportClick}
                disabled={!!error || isLoading || !!billingRatesError}
                id={exportButtonId}
                loading={isExporting}
              >
                Export
              </Button>
            </DropdownPanel>
          </div>
        </div>
      }
      footerVariant="alternative"
    >
      <CoverageSearch legacyPlan={legacyPlan} variant="modal" />
    </Modal>
  );
};

export default CoverageSearchModal;
